import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Brain, Code, Cloud, Shield, BarChart3, Network, GraduationCap, FileText, ChevronRight } from 'lucide-react';
export function Services() {
  const location = useLocation();
  useEffect(() => {
    // Handle hash navigation
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
      }
    }
  }, [location]);
  return <div className="min-h-screen bg-white pt-20" data-id="element-660">
      {/* Hero Section with Beautiful Header Image */}
      <section className="relative py-20 sm:py-32 bg-gradient-to-br from-slate-900 to-blue-900 text-white overflow-hidden" data-id="element-661">
        <div className="absolute inset-0 opacity-30" data-id="element-662">
          <img src="https://images.unsplash.com/photo-1573164574511-73c773193279?q=80&w=2000&auto=format&fit=crop" alt="Black woman working with advanced AI technology and data analytics" className="w-full h-full object-cover" data-id="element-663" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-blue-900/80" data-id="element-664" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center" data-id="element-665">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6" data-id="element-666">
            Our Services
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed" data-id="element-667">
            Comprehensive technology solutions designed to accelerate your
            digital transformation journey
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20" data-id="element-668">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-16 sm:space-y-24" data-id="element-669">
          {/* AI & Machine Learning */}
          <ServiceSection id="ai" icon={Brain} title="Artificial Intelligence & Machine Learning" description="Harness the power of AI to automate processes, gain insights, and make data-driven decisions" services={['Predictive Analytics & Forecasting', 'Natural Language Processing (NLP)', 'Computer Vision Solutions', 'Chatbots & Virtual Assistants', 'Recommendation Systems', 'Automated Decision Making', 'Data-Driven Process Automation', 'Custom ML Model Development']} color="blue" data-id="element-670" />

          {/* Software Development */}
          <ServiceSection id="software" icon={Code} title="Website & Software Development" description="Custom-built applications tailored to your unique business requirements" services={['Custom Website Development', 'E-commerce Platforms', 'Enterprise Web Portals', 'Progressive Web Apps (PWA)', 'Mobile App Development (iOS & Android)', 'API Development & Integration', 'Legacy System Modernization', 'SaaS Product Development']} color="cyan" data-id="element-671" />

          {/* Cloud Solutions */}
          <ServiceSection id="cloud" icon={Cloud} title="Digital Transformation & Cloud Solutions" description="Scalable, secure cloud infrastructure for modern businesses" services={['Cloud Migration & Strategy', 'AWS, Azure, Google Cloud Setup', 'Cloud-Native Architecture', 'DevOps & CI/CD Implementation', 'Microservices Architecture', 'Serverless Computing', 'Business Process Automation', 'Digital Workflow Optimization']} color="indigo" data-id="element-672" />

          {/* Cybersecurity */}
          <ServiceSection id="security" icon={Shield} title="Cybersecurity & Data Protection" description="Comprehensive security solutions to protect your digital assets" services={['Security Audits & Assessments', 'Threat Detection & Response', 'Data Encryption & Protection', 'Compliance Management (GDPR, ISO)', 'Penetration Testing', 'Security Operations Center (SOC)', 'Incident Response Planning', 'Employee Security Training']} color="red" data-id="element-673" />

          {/* Data Analytics */}
          <ServiceSection id="analytics" icon={BarChart3} title="Data Analytics & Business Intelligence" description="Transform raw data into actionable business insights" services={['Business Intelligence Dashboards', 'Data Warehousing Solutions', 'Real-Time Analytics', 'Customer Behavior Analysis', 'Performance Metrics & KPIs', 'Data Visualization', 'Predictive Modeling', 'Big Data Processing']} color="green" data-id="element-674" />

          {/* Networking & IT Infrastructure */}
          <ServiceSection id="networking" icon={Network} title="Networking & IT Infrastructure" description="Robust, scalable IT infrastructure to support your operations" services={['Network Design & Implementation', 'Server Setup & Management', 'IT Infrastructure Consulting', 'Hardware Procurement', 'Network Security Configuration', 'Disaster Recovery Planning', 'IT Support & Maintenance', 'System Monitoring & Optimization']} color="purple" data-id="element-675" />

          {/* IT Consulting & Training */}
          <ServiceSection id="consulting" icon={GraduationCap} title="IT Consulting & Training" description="Strategic guidance and capacity building for your technology initiatives" services={['Digital Transformation Strategy', 'Technology Roadmap Planning', 'IT Governance & Compliance', 'Change Management', 'Staff Training & Workshops', 'Technology Assessment', 'Vendor Selection & Management', 'Project Management Consulting']} color="orange" data-id="element-676" />

          {/* Business Writing */}
          <ServiceSection id="writing" icon={FileText} title="Professional Business Writing Services" description="Compelling business documents that communicate your value and vision" services={['Company Profiles', 'Business Plans & Proposals', 'Pitch Decks & Presentations', 'Grant Writing & Applications', 'Standard Operating Procedures (SOPs)', 'Technical Documentation', 'Marketing Content', 'Executive Summaries']} color="teal" data-id="element-677" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 bg-slate-50" data-id="element-678">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center" data-id="element-679">
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-4 sm:mb-6" data-id="element-680">
            Ready to Get Started?
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 mb-8 sm:mb-10" data-id="element-681">
            Let's discuss how our services can help transform your business
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-base sm:text-lg transition-all duration-300 hover:scale-105" data-id="element-682">
            Request a Consultation
            <ChevronRight className="w-5 h-5" data-id="element-683" />
          </Link>
        </div>
      </section>
    </div>;
}
function ServiceSection({
  id,
  icon: Icon,
  title,
  description,
  services,
  color
}: {
  id: string;
  icon: any;
  title: string;
  description: string;
  services: string[];
  color: string;
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    cyan: 'bg-cyan-100 text-cyan-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    red: 'bg-red-100 text-red-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    teal: 'bg-teal-100 text-teal-600'
  };
  return <div id={id} className="scroll-mt-24" data-id="element-684">
      <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start" data-id="element-685">
        <div className="lg:sticky lg:top-32" data-id="element-686">
          <div className={`inline-flex w-12 h-12 sm:w-16 sm:h-16 ${colorClasses[color as keyof typeof colorClasses]} rounded-2xl items-center justify-center mb-4 sm:mb-6`} data-id="element-687">
            <Icon className="w-6 h-6 sm:w-8 sm:h-8" data-id="element-688" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-3 sm:mb-4" data-id="element-689">
            {title}
          </h2>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed" data-id="element-690">
            {description}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-691">
          {services.map((service, index) => <div key={index} className="p-4 sm:p-6 bg-slate-50 hover:bg-white border-2 border-transparent hover:border-slate-200 rounded-xl transition-all duration-300 hover:shadow-lg" data-id="element-692">
              <div className="flex items-start gap-2 sm:gap-3" data-id="element-693">
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-694" />
                <span className="font-bold text-slate-800 text-sm sm:text-base" data-id="element-695">
                  {service}
                </span>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}